import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { motion } from 'framer-motion';
import { CalendarCheck, CheckCircle, XCircle, RefreshCw, Trophy } from 'lucide-react';
import axios from 'axios';
import { trackEvent } from '../lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: "'Outfit', sans-serif" };

export default function DailyQuiz() {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/daily/quiz`).then((r) => setData(r.data)).catch(() => {});
  }, []);

  const questions = data?.questions || [];
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);
  const score = questions.filter((q) => answers[q.id] === q.correct_answer).length;

  const submit = () => {
    setSubmitted(true);
    trackEvent('quiz_complete', { quiz: 'daily', score, total: questions.length });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-center gap-4">
          <div className="w-14 h-14 bg-[#C0975C] flex items-center justify-center rounded-xl">
            <CalendarCheck className="w-7 h-7 text-[#0A1F44]" />
          </div>
          <div>
            <h1 className="text-3xl font-bold" style={heading}>Daily <span className="text-[#C0975C]">Quiz</span></h1>
            <p className="text-[#94A9C9]">10 questions across every area of financial planning. New set every day.</p>
          </div>
        </motion.div>

        {submitted && (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="mb-8 rounded-2xl p-6 text-center border border-[#C0975C]/40 bg-gradient-to-r from-[#122C5A] to-[#0A1F44]" data-testid="daily-quiz-result">
            <Trophy className="w-10 h-10 mx-auto mb-2 text-[#C0975C]" />
            <p className="text-4xl font-bold text-[#C0975C]" style={heading}>{score}/{questions.length}</p>
            <p className="text-[#94A9C9] mt-1">
              {score === questions.length ? 'Perfect. Your money knowledge is sharp.' : score >= 6 ? 'Strong work. Keep learning.' : 'Good start. Review the answers below.'}
            </p>
          </motion.div>
        )}

        {!data ? (
          <div className="text-center py-16 text-[#C0975C] animate-pulse">Loading today's quiz...</div>
        ) : (
          <div className="space-y-5" data-testid="daily-quiz-list">
            {questions.map((q, qi) => (
              <div key={`${q.id}-${qi}`} className="neo-card p-5 lg:p-6">
                <p className="text-xs uppercase tracking-wider text-[#C0975C] mb-2">Question {qi + 1} · {q.category}</p>
                <p className="text-white font-semibold mb-4" style={heading}>{q.question}</p>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[q.id] === oi;
                    const correct = q.correct_answer === oi;
                    let cls = 'quiz-option';
                    if (submitted && correct) cls += ' correct';
                    else if (submitted && chosen && !correct) cls += ' incorrect';
                    else if (chosen) cls += ' selected';
                    return (
                      <button
                        key={oi}
                        disabled={submitted}
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                        className={`${cls} w-full text-left flex items-center justify-between text-sm text-white`}
                        data-testid={`q-${q.id}-opt-${oi}`}
                      >
                        <span>{opt}</span>
                        {submitted && correct && <CheckCircle className="w-4 h-4 text-[#C0975C]" />}
                        {submitted && chosen && !correct && <XCircle className="w-4 h-4 text-[#FF6b6b]" />}
                      </button>
                    );
                  })}
                </div>
                {submitted && (
                  <p className="text-xs text-[#94A9C9] mt-3 bg-[#061530] border border-[#1D3D72] rounded-lg p-3">{q.explanation}</p>
                )}
              </div>
            ))}

            {!submitted ? (
              <button
                onClick={submit}
                disabled={!allAnswered}
                data-testid="daily-quiz-submit"
                className="neo-button-primary w-full py-4 text-lg disabled:opacity-50"
              >
                {allAnswered ? 'Submit answers' : `Answer all ${questions.length} questions`}
              </button>
            ) : (
              <button onClick={() => { setSubmitted(false); setAnswers({}); }} className="neo-button-secondary w-full py-4 inline-flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4" /> Try again
              </button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
